import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  createEquipment,
  deleteEquipment,
  editEquipment,
  getEquipments,
} from "../../../api/equipment";
import RemoveModal from "../../modals/removeModal";
import CreateOrEditEquipmentModal from "./CreateOrEditEquipmentModal";

// Material UI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import { alpha, useTheme } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";

import { dispatchSnackBarMessage } from "../../../utils/snackbar";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.2,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

const EquipmentList = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [equipments, setEquipments] = useState([]);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [showCreateOrEditEquipmentModal, setShowCreateOrEditEquipmentModal] =
    useState(false);

  const [equipmentObjToDelete, setEquipmentObjToDelete] = useState(null);
  const [showEquipmentDeleteModal, setShowEquipmentDeleteModal] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setTimeout(async () => {
      await loadEquipments();
    }, 10);
  }, []);

  const loadEquipments = async () => {
    setLoading(true);
    try {
      const res = await getEquipments();
      setEquipments(res.data);
    } catch (error) {
      setErrorMessage(
        "Não foi possível carregar os equipamentos. Tente novamente mais tarde."
      );
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEditModal = () => {
    setShowCreateOrEditEquipmentModal(!showCreateOrEditEquipmentModal);
    setEditingEquipment(null);
  };

  const handleCreateEquipment = async (equipmentObj) => {
    try {
      await createEquipment(equipmentObj);
      handleCreateEditModal();
      await loadEquipments();
      dispatchSnackBarMessage(
        `create-equipment-success-${new Date()}`,
        "Equipamento cadastrado com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }
      dispatchSnackBarMessage(
        `create-equipment-${new Date()}`,
        messages || "Erro ao cadastrar equipamento",
        "error"
      );
    }
  };

  const handleEditEquipment = (equipment) => {
    setEditingEquipment(equipment);
    setShowCreateOrEditEquipmentModal(!showCreateOrEditEquipmentModal);
  };

  const confirmEditEquipment = async (equipmentObj) => {
    try {
      await editEquipment(equipmentObj, equipmentObj.id);
      handleCreateEditModal();
      setEditingEquipment(null);
      await loadEquipments();
      dispatchSnackBarMessage(
        `edit-equipment-success-${new Date()}`,
        "Equipamento atualizado com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }
      dispatchSnackBarMessage(
        `edit-equipment-${new Date()}`,
        messages || "Erro ao editar equipamento",
        "error"
      );
    }
  };

  const handleDeleteEquipmentModal = (equipment) => {
    setEquipmentObjToDelete(equipment);
    setShowEquipmentDeleteModal(!showEquipmentDeleteModal);
  };

  const handleDeleteEquipment = async () => {
    try {
      await deleteEquipment(equipmentObjToDelete.id);
      setShowEquipmentDeleteModal(false);
      setEquipmentObjToDelete(null);
      await loadEquipments();
      dispatchSnackBarMessage(
        `delete-equipment-success-${new Date()}`,
        "Equipamento removido com sucesso!",
        "success"
      );
    } catch (error) {
      dispatchSnackBarMessage(
        `delete-equipment-error-${new Date()}`,
        "Erro ao remover equipamento",
        "error"
      );
      setShowEquipmentDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Box sx={{ p: 3 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              sx={{ mb: 2, borderRadius: 2 }}
            />
            {[1, 2, 3].map((item) => (
              <Skeleton
                key={item}
                variant="rectangular"
                width="100%"
                height={60}
                sx={{ mb: 2, borderRadius: 2 }}
              />
            ))}
          </Box>
        </motion.div>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Collapse in={showError} sx={{ mb: 2 }}>
          <Alert
            severity="error"
            onClose={() => setShowError(false)}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        </Collapse>

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={handleCreateEditModal}
                color="primary"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: "medium",
                  boxShadow: theme.shadows[3],
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                }}
              >
                Cadastrar Novo Equipamento
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        <Box
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            borderRadius: 3,
            p: 2,
            boxShadow: theme.shadows[1],
            backdropFilter: "blur(8px)",
          }}
        >
          {equipments.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  px: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  borderStyle: "dashed",
                  borderWidth: 1,
                  borderColor: theme.palette.divider,
                }}
              >
                <SettingsRemoteIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.action.disabled,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Nenhum equipamento cadastrado
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  sx={{ mb: 2, maxWidth: 400 }}
                >
                  Cadastre um novo equipamento para começar a gerenciar suas
                  maquininhas de pagamento.
                </Typography>
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={handleCreateEditModal}
                    sx={{ mt: 1, borderRadius: 2 }}
                  >
                    Adicionar Equipamento
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <AnimatePresence>
                {equipments.map((equipmentItem) => (
                  <motion.div
                    key={equipmentItem.id}
                    variants={itemVariants}
                    exit="exit"
                    layout
                  >
                    <Card
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        overflow: "visible",
                        boxShadow: theme.shadows[2],
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: theme.shadows[5],
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{
                            p: 2,
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                              flexGrow: 1,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                ),
                                borderRadius: "50%",
                                p: 1,
                                mr: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <SettingsRemoteIcon color="primary" />
                            </Box>
                            <Box>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: "medium",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "30ch",
                                }}
                              >
                                {equipmentItem.name}
                              </Typography>
                              <Chip
                                size="small"
                                label={equipmentItem.type || "Maquininha"}
                                color="default"
                                sx={{
                                  height: 24,
                                  backgroundColor: alpha(
                                    theme.palette.secondary.main,
                                    0.1
                                  ),
                                  color: theme.palette.secondary.dark,
                                  mt: 0.5,
                                }}
                              />
                            </Box>
                          </Box>

                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Editar equipamento">
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <IconButton
                                  size="small"
                                  aria-label="edit"
                                  color="primary"
                                  onClick={() =>
                                    handleEditEquipment(equipmentItem)
                                  }
                                  sx={{
                                    backgroundColor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    "&:hover": {
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.2
                                      ),
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </motion.div>
                            </Tooltip>
                            <Tooltip title="Excluir equipamento">
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <IconButton
                                  size="small"
                                  aria-label="delete"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteEquipmentModal(equipmentItem)
                                  }
                                  sx={{
                                    backgroundColor: alpha(
                                      theme.palette.error.main,
                                      0.1
                                    ),
                                    "&:hover": {
                                      backgroundColor: alpha(
                                        theme.palette.error.main,
                                        0.2
                                      ),
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </motion.div>
                            </Tooltip>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </Box>
      </Box>

      <RemoveModal
        title="Excluir equipamento"
        message={`Tem certeza que deseja excluir o equipamento "${equipmentObjToDelete?.name}"?`}
        show={showEquipmentDeleteModal}
        toggleModal={handleDeleteEquipmentModal}
        onRemove={handleDeleteEquipment}
      />

      {showCreateOrEditEquipmentModal && (
        <CreateOrEditEquipmentModal
          show={showCreateOrEditEquipmentModal}
          closeModal={handleCreateEditModal}
          createEquipment={handleCreateEquipment}
          editEquipment={confirmEditEquipment}
          equipment={editingEquipment}
        />
      )}
    </>
  );
};

export default EquipmentList;
